import {
  type IDefinitionService,
  type IDrawService,
  type IGetDrawUseCase,
  NotFoundError,
  NotInitializedError,
} from '../../../domain/entry.js';

/**
 * Use case for getting a specific draw given a product.
 *
 * @param drawService Draw service dependency
 * @param definitionService Definition service dependency
 * @returns Use case executor for retrieving opened draws
 */
export function getDrawUseCase(drawService: IDrawService, definitionService: IDefinitionService): IGetDrawUseCase {
  return {
    execute: async ({ product, drawNumber }): ReturnType<IGetDrawUseCase['execute']> => {
      const definition = await definitionService.resolveDefinitionAsync(product);

      if (!definition) {
        throw new NotInitializedError(`Unable to load definition for product: ${product}`);
      }

      const openedDrawsCache = drawService.getOpenedDrawsCache(definition.moduleEngine);

      if (openedDrawsCache) {
        const cachedDraw = openedDrawsCache.find(
          ({ draw }) => draw.drawNumber === drawNumber && draw.product === product
        );

        if (cachedDraw) {
          return cachedDraw;
        }
      }

      const drawData = await drawService.getDrawAsync(product, { drawNumber });

      if (!drawData) {
        throw new NotFoundError(`Draw ${drawNumber} for ${product} not found`);
      }

      return {
        draw: drawData.draw,
        bettingEvent: drawData.bettingEvent,
        fund: drawData.fund,
        leagues: drawData.leagues,
        participants: drawData.participants,
        countries: drawData.countries,
        sport: drawData.sport,
        eventTypeStatistics: drawData.eventTypeStatistics,
      };
    },
  };
}
